import { GetWorkflowDefinitionsResponse } from "../Types/get-workflow-definitions-response";
import { CreateInstanceRequest } from "../Types/create-instance-request";
import { CreateInstanceResponse } from "../Types/create-instance-response";
import { CreateInstancesResponse } from "../Types/create-instances-response";
import { DbWorkflowDefinitionInfos } from "../Types/db-workflow-definition-infos";
import { DbWorkflowUserTasksInfos } from "../Types/db-workflow-user-tasks-infos";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetWorkflowInstanceDetailsResponse } from "../Types/get-workflow-instance-details-response";
import { GetWorkflowInstanceEnumsResponse } from "../Types/get-workflow-instance-enums-response";
import { GetWorkflowInstanceResponse } from "../Types/get-workflow-instance-response";
import { ListWorkflowInstanceResponse } from "../Types/list-workflow-instance-response";
import { SetWorkflowInstanceFollowersRequest } from "../Types/set-workflow-instance-followers-request";
import { WorkflowStatus } from "../Types/workflow-status";
import { AclWorkflowAccessLevel } from "../Types/acl-workflow-access-level";
import { IApiClient } from "../api-client";
import { EnvType } from "../services";
import { DbKalikoWorkflowModelInfos, GetKalikoBPMWorkflowExecResponse } from "../Types/kaliko";
import { ListNodeResponse } from "../Types/list-node-response";
import { WorkflowTag } from "../Types/workflow-tag";

interface IWorkflowApiClient {
  GetDefinitions(): Promise<GetWorkflowDefinitionsResponse>;
  GetDefinitionsByObject(objectId: number): Promise<GetWorkflowDefinitionsResponse>;
  CreateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd>;
  UpdateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd>;
  DeleteDefinition(id: number): Promise<GenericResponseStd>;
  GetInstancesByDefinitionType(
    definitionType: string,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByDefinitionTypeAndAccessLevel(
    definitionType: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByDefinitionTypeAndDate(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByDefinitionTypeDateAndAccessLevel(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse>;

  GetInstancesByFamily(
    definitionFamily: string,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByFamilyAndAccessLevel(
    definitionFamily: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByFamilyAndDays(
    definitionFamily: string,
    days: number,
    tag: WorkflowTag | undefined
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByFamilyDaysAndAccessLevel(
    definitionFamily: string,
    days: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByFamilyAndDate(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse>;
  GetInstancesByFamilyDateAndAccessLevel(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse>;

  DownloadInstancesByDefinitionFamilyAsCSV(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<GenericResponseStd>;
  GetInstancesByAssetId(assetId: number): Promise<ListWorkflowInstanceResponse>;
  GetInstanceById(instanceId: number): Promise<GetWorkflowInstanceDetailsResponse>;
  CreateInstance(request: CreateInstanceRequest): Promise<CreateInstanceResponse>;
  CreateInstances(requests: CreateInstanceRequest[]): Promise<CreateInstancesResponse>;
  PlayInstance(instanceId: number, inboundVars?: Record<string, string>): Promise<GenericResponseStd>;
  RetryInstance(instanceId: number, fromBeggining: boolean): Promise<GenericResponseStd>;
  RetryInstanceScratch(instanceId: number): Promise<GenericResponseStd>;
  AbortInstance(instanceId: number): Promise<GenericResponseStd>;
  DeleteInstance(instanceId: number): Promise<GenericResponseStd>;
  SetPriorityOnInstanceById(instanceId: number, priority: number): Promise<GenericResponseStd>;
  NotifyResync(instanceId: number): Promise<GenericResponseStd>;
  SetWorkflowInstanceFollowers(request: SetWorkflowInstanceFollowersRequest): Promise<GenericResponseStd>;
  GetUserTaskEnums(): Promise<GetWorkflowInstanceEnumsResponse>;
  ListUserTasks(): Promise<ListWorkflowInstanceResponse>;
  ListUserTasksByPages(pageNb: number, pageSize: number): Promise<ListWorkflowInstanceResponse>;
  GetUserTaskByInstanceId(instanceId: number): Promise<GetWorkflowInstanceResponse>;
  ListUserTaskByObjectId(objectId: number): Promise<ListWorkflowInstanceResponse>;
  ListUserTaskByStatus(status: WorkflowStatus): Promise<ListWorkflowInstanceResponse>;
  ClaimUserTask(instanceId: number): Promise<GenericResponseStd>;
  ClaimUserTaskByUserId(instanceId: number, userId: number): Promise<GenericResponseStd>;
  UpdateUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd>;
  CompleteUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd>;
}

export default class WorkflowApiClient implements IWorkflowApiClient {
  readonly servicePort = 9504;
  readonly serviceBaseUrl = "workflow-service/api";

  workflowApiClient: IApiClient;

  constructor(workflowApiClient: IApiClient, envType: EnvType) {
    this.workflowApiClient = workflowApiClient;
    if (envType === "local") this.workflowApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetDefinitions(): Promise<GetWorkflowDefinitionsResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/definitions`);
  }
  async GetDefinitionsByObject(objectId: number): Promise<GetWorkflowDefinitionsResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/definitions/object/${objectId}`);
  }
  async CreateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/definitions`, request);
  }
  async UpdateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/definitions`, request);
  }
  async DeleteDefinition(id: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.delete(`${this.serviceBaseUrl}/definitions/${id}`);
  }
  async GetInstancesByDefinitionType(
    definitionType: string,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/types/${definitionType}/${pageNb}/${pageSize}`
    );
  }
  async GetInstancesByDefinitionTypeAndAccessLevel(
    definitionType: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/types/${definitionType}/${pageNb}/${pageSize}/${workflowAccessLevel}`
    );
  }
  async GetInstancesByDefinitionTypeAndDate(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/types/${definitionType}/dates/${startDateAsString}/${endDateAsString}`
    );
  }
  async GetInstancesByDefinitionTypeDateAndAccessLevel(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/types/${definitionType}/dates/${startDateAsString}/${endDateAsString}/${workflowAccessLevel}`
    );
  }
  async GetInstancesByFamily(
    definitionFamily: string,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/${pageNb}/${pageSize}`
    );
  }
  async GetInstancesByFamilyAndAccessLevel(
    definitionFamily: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/${pageNb}/${pageSize}/${workflowAccessLevel}`
    );
  }
  async GetInstancesByFamilyAndDays(
    definitionFamily: string,
    days: number,
    tag: WorkflowTag | undefined
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/days/${days}${tag ? `/tag/${tag}` : ""}`
    );
  }
  async GetInstancesByFamilyDaysAndAccessLevel(
    definitionFamily: string,
    days: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/days/${days}/${workflowAccessLevel}`
    );
  }
  async GetInstancesByFamilyAndDate(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/dates/${startDateAsString}/${endDateAsString}`
    );
  }
  async GetInstancesByFamilyDateAndAccessLevel(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/dates/${startDateAsString}/${endDateAsString}/${workflowAccessLevel}`
    );
  }
  async DownloadInstancesByDefinitionFamilyAsCSV(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(
      `${this.serviceBaseUrl}/instances/families/${definitionFamily}/dates/${startDateAsString}/${endDateAsString}/csv`
    );
  }
  async GetInstancesByAssetId(assetId: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/instances/assets/${assetId}`);
  }
  async GetInstanceById(instanceId: number): Promise<GetWorkflowInstanceDetailsResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/instances/${instanceId}`);
  }
  async CreateInstance(request: CreateInstanceRequest): Promise<CreateInstanceResponse> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances`, request);
  }
  async CreateInstances(requests: CreateInstanceRequest[]): Promise<CreateInstancesResponse> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/multi`, requests);
  }
  async PlayInstance(instanceId: number, inboundVars?: Record<string, string>): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/play/${instanceId}`, inboundVars);
  }
  async RetryInstance(instanceId: number, fromBeggining: boolean = false): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/retry/${instanceId}`, fromBeggining);
  }
  async RetryInstanceScratch(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/retry-scratch/${instanceId}`);
  }
  async AbortInstance(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/abort/${instanceId}`);
  }
  async DeleteInstance(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.delete(`${this.serviceBaseUrl}/instances/delete/${instanceId}`);
  }
  async SetPriorityOnInstanceById(instanceId: number, priority: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/instances/${instanceId}/priority/${priority}`);
  }
  async NotifyResync(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/instances/notify-resync/${instanceId}`);
  }
  async SetWorkflowInstanceFollowers(request: SetWorkflowInstanceFollowersRequest): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/instances/followers`, request);
  }
  async GetUserTaskEnums(): Promise<GetWorkflowInstanceEnumsResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks/enums`);
  }
  async ListUserTasks(): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks`);
  }
  async ListUserTasksByPages(pageNb: number, pageSize: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks/${pageNb}/${pageSize}`);
  }
  async GetUserTaskByInstanceId(instanceId: number): Promise<GetWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks/${instanceId}`);
  }
  async ListUserTaskByObjectId(objectId: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks/object/${objectId}`);
  }
  async ListUserTaskByStatus(status: WorkflowStatus): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/user-tasks/status/${status}`);
  }
  async ClaimUserTask(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/user-tasks/claim/${instanceId}`, instanceId);
  }
  async UnclaimUserTask(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/user-tasks/unclaim/${instanceId}`, instanceId);
  }
  async ClaimUserTaskByUserId(instanceId: number, userId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/user-tasks/claim/${instanceId}/${userId}`);
  }
  async UpdateUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/user-tasks/metadata/${workflowId}`, userTask);
  }
  async CompleteUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/user-tasks/complete/${workflowId}`, userTask);
  }
  async GetNodes(): Promise<ListNodeResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/bpm-kaliko/nodes`);
  }
  async GetWorkflowModels(): Promise<DbKalikoWorkflowModelInfos[]> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/bpm-kaliko/workflow-models`);
  }
  async UpdateWorkflowModels(workflow: DbKalikoWorkflowModelInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.put(`${this.serviceBaseUrl}/bpm-kaliko/workflow-models`, workflow);
  }
  async CreateWorkflowModels(workflow: DbKalikoWorkflowModelInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.post(`${this.serviceBaseUrl}/bpm-kaliko/workflow-models`, workflow);
  }
  async GetKalikoBPMWorkflowExecById(id: number): Promise<GetKalikoBPMWorkflowExecResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/bpm-kaliko/workflow-execution/id/${id}`);
  }
  async GetKalikoBPMWorkflowExecByGuid(guid: string): Promise<GetKalikoBPMWorkflowExecResponse> {
    return await this.workflowApiClient.get(`${this.serviceBaseUrl}/bpm-kaliko/workflow-execution/guid/${guid}`);
  }
  async DeleteKalikoBPMWorkflowModel(id: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.delete(`${this.serviceBaseUrl}/bpm-kaliko/workflow-models/${id}`);
  }
  //------Utils------
  GetApiUrl(): string {
    return this.workflowApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
